import { motion } from 'motion/react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useTranslation } from '../i18n';

interface CTABannerProps {
  onNavigateToBooking: () => void;
}

export function CTABanner({ onNavigateToBooking }: CTABannerProps) {
  const { t } = useTranslation();
  return (
    <section className="py-24 bg-gradient-to-br from-[#0A1A33] via-[#0A1A33] to-[#0A1A33]/90 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C6A667] rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C6A667] rounded-full blur-3xl" />
      </div>

      {/* Floating Icons */}
      <motion.div
        className="absolute top-20 right-20"
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
      >
        <Sparkles className="w-12 h-12 text-[#C6A667]/30" />
      </motion.div>

      <motion.div
        className="absolute bottom-20 left-20"
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 7, repeat: Infinity }}
      >
        <Sparkles className="w-16 h-16 text-[#C6A667]/20" />
      </motion.div>

      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-white mb-4 font-bold text-[32px]">{t('ctaBanner.title')}</h2>
          <p className="text-[#C6A667] mb-8 opacity-90">{t('ctaBanner.subtitle')}</p>
          
          <p className="text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('ctaBanner.description')}
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onNavigateToBooking()}
            className="group bg-[#C6A667] hover:bg-[#D4B578] text-[#0A1A33] px-10 py-5 rounded-full transition-all duration-300 flex items-center gap-3 shadow-2xl hover:shadow-[#C6A667]/50 mx-auto"
          >
            <span className="text-lg not-italic font-bold font-normal text-[24px]">{t('ctaBanner.cta')}</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
